import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import { useSearch } from '../contexts/SearchContext';
import '../components/Button.css';
import './Leveranciers.css';

const Leveranciers = () => {
  const [leveranciers, setLeveranciers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { searchTerm } = useSearch();
  const [showModal, setShowModal] = useState(false);
  const [editingLeverancier, setEditingLeverancier] = useState(null);
  const [formData, setFormData] = useState({
    naam: '',
    contactpersoon: '',
    email: '',
    telefoon: '',
    adres: '',
    levertijd_dagen: '',
    minimale_inkoop_per_order: '',
    minimale_cbm_per_order: '',
    valuta: 'EUR',
    prijsberekening_marge_pct: ''
  });

  useEffect(() => {
    fetchLeveranciers();
  }, []);

  const fetchLeveranciers = async () => {
    try {
      const response = await api.get('/leveranciers');
      setLeveranciers(response.data);
    } catch (error) {
      console.error('Error fetching leveranciers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLeverancier) {
        await api.put(`/leveranciers/${editingLeverancier.id}`, formData);
      } else {
        await api.post('/leveranciers', formData);
      }

      setShowModal(false);
      resetForm();
      fetchLeveranciers();
    } catch (error) {
      console.error('Error saving leverancier:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (leverancier) => {
    setEditingLeverancier(leverancier);
    setFormData({
      naam: leverancier.naam || '',
      contactpersoon: leverancier.contactpersoon || '',
      email: leverancier.email || '',
      telefoon: leverancier.telefoon || '',
      adres: leverancier.adres || '',
      levertijd_dagen: leverancier.levertijd_dagen || '',
      minimale_inkoop_per_order: leverancier.minimale_inkoop_per_order || '',
      minimale_cbm_per_order: leverancier.minimale_cbm_per_order || '',
      valuta: leverancier.valuta || 'EUR',
      prijsberekening_marge_pct:
        leverancier.prijsberekening_marge_pct != null && leverancier.prijsberekening_marge_pct !== ''
          ? String(leverancier.prijsberekening_marge_pct)
          : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze leverancier wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/leveranciers/${id}`);
      fetchLeveranciers();
    } catch (error) {
      console.error('Error deleting leverancier:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      naam: '',
      contactpersoon: '',
      email: '',
      telefoon: '',
      adres: '',
      levertijd_dagen: '',
      minimale_inkoop_per_order: '',
      minimale_cbm_per_order: '',
      valuta: 'EUR',
      prijsberekening_marge_pct: ''
    });
    setEditingLeverancier(null);
  };

  // Filter leveranciers op basis van zoekterm
  const filteredLeveranciers = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) {
      return leveranciers;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    
    return leveranciers.filter(leverancier => {
      const naam = String(leverancier.naam || '').toLowerCase();
      const contactpersoon = String(leverancier.contactpersoon || '').toLowerCase();
      const email = String(leverancier.email || '').toLowerCase();
      const telefoon = String(leverancier.telefoon || '').toLowerCase();
      const adres = String(leverancier.adres || '').toLowerCase();
      
      return naam.includes(searchLower) || 
             contactpersoon.includes(searchLower) ||
             email.includes(searchLower) ||
             telefoon.includes(searchLower) ||
             adres.includes(searchLower);
    });
  }, [leveranciers, searchTerm]);

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="leveranciers">
      <div className="page-header">
        <h1 className="page-title">Leveranciers</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <FiPlus /> Nieuwe Leverancier
        </button>
      </div>

      {searchTerm && (
        <div className="search-results-info" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF' }}>
          <strong>{filteredLeveranciers.length}</strong> van <strong>{leveranciers.length}</strong> leverancier(s) gevonden voor "{searchTerm}"
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Contactpersoon</th>
              <th>Email</th>
              <th>Telefoon</th>
              <th>Adres</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeveranciers.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  {searchTerm ? `Geen leveranciers gevonden voor "${searchTerm}"` : 'Geen leveranciers gevonden'}
                </td>
              </tr>
            ) : (
              filteredLeveranciers.map((leverancier) => (
                <tr key={leverancier.id}>
                  <td><strong>{leverancier.naam}</strong></td>
                  <td>{leverancier.contactpersoon || '-'}</td>
                  <td>{leverancier.email || '-'}</td>
                  <td>{leverancier.telefoon || '-'}</td>
                  <td>{leverancier.adres || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" onClick={() => handleEdit(leverancier)}>
                        <FiEdit />
                      </button>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(leverancier.id)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editingLeverancier ? 'Leverancier Bewerken' : 'Nieuwe Leverancier'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Naam *</label>
            <input
              type="text"
              value={formData.naam}
              onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Contactpersoon</label>
            <input
              type="text"
              value={formData.contactpersoon}
              onChange={(e) => setFormData({ ...formData, contactpersoon: e.target.value })}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Telefoon</label>
              <input
                type="tel"
                value={formData.telefoon}
                onChange={(e) => setFormData({ ...formData, telefoon: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Adres</label>
            <textarea
              value={formData.adres}
              onChange={(e) => setFormData({ ...formData, adres: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Levertijd (dagen)</label>
              <input
                type="number"
                value={formData.levertijd_dagen}
                onChange={(e) => setFormData({ ...formData, levertijd_dagen: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Minimale Inkoop per Order (€)</label>
              <input
                type="number"
                step="0.01"
                value={formData.minimale_inkoop_per_order}
                onChange={(e) => setFormData({ ...formData, minimale_inkoop_per_order: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Minimale CBM per Order</label>
              <input
                type="number"
                step="0.001"
                value={formData.minimale_cbm_per_order}
                onChange={(e) => setFormData({ ...formData, minimale_cbm_per_order: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Valuta</label>
              <select
                value={formData.valuta}
                onChange={(e) => setFormData({ ...formData, valuta: e.target.value })}
              >
                <option value="EUR">Euro (€)</option>
                <option value="USD">US Dollar ($)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Prijsberekening: marge % (optioneel)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.prijsberekening_marge_pct}
              onChange={(e) => setFormData({ ...formData, prijsberekening_marge_pct: e.target.value })}
              placeholder="Leeg = standaard uit tab Producten › Prijsberekening"
            />
            <small style={{ color: '#666', display: 'block', marginTop: '0.35rem' }}>
              Overschrijft de standaard marge % voor alle producten van deze leverancier (op kostprijs + extra + verzend, vóór
              marketing).
            </small>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary">
              {editingLeverancier ? 'Bijwerken' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Leveranciers;
